/**
 * WizardV2 - Main Wizard Orchestrator (v2 Sidebar Layout)
 * 
 * Refactored version with extracted hooks for better maintainability:
 * - useWizardState: State management, navigation, localStorage backup
 * - useReportLoader: Loading existing reports when resuming
 * - useAutoSave: Auto-saving drafts to backend
 * 
 * This is a parallel implementation of the wizard with the new sidebar layout.
 * The original Wizard.tsx remains untouched as a fallback.
 */

import React from 'react';
import { useAutoSave } from '../../../hooks/useAutoSave';

// Custom hooks
import { useWizardState, useReportLoader } from './hooks';

// V2 Sidebar Steps
import SidebarStep1 from './SidebarStep1';
import SidebarStep2 from './SidebarStep2';
import SidebarStep3 from './SidebarStep3';
import SidebarStep4 from './SidebarStep4';
import SidebarStep4_FutureClosure from './SidebarStep4_FutureClosure';
import SidebarStep5 from './SidebarStep5';
import SidebarStep5_FutureClosure from './SidebarStep5_FutureClosure';
import SidebarStep6 from './SidebarStep6';
import SidebarStep6_FutureClosure from './SidebarStep6_FutureClosure';
import SidebarStep7 from './SidebarStep7';
import SidebarStep7_FutureClosure from './SidebarStep7_FutureClosure';

export default function WizardV2() {
  // Use extracted state management hook
  const {
    step,
    data,
    showRestoredBanner,
    highestCompletedStep,
    loadingUrlDraft,
    resumeReportId,
    urlDraftId,
    urlSection,
    setStep,
    setData,
    updateData,
    next,
    back,
    goToStep,
    clearAndStartFresh,
    setShowRestoredBanner,
    setHighestCompletedStep,
  } = useWizardState();

  // Load report data when resuming
  useReportLoader({
    resumeReportId,
    setData,
    setStep,
    setHighestCompletedStep,
  });

  // Auto-save hook
  const { 
    draftId,
    lastSaved, 
    isSaving, 
    deleteDraft,
    saveNow
  } = useAutoSave({
    step,
    data,
    enabled: true,
    reportId: resumeReportId
  });
  
  // Compute effective claim ID for QA Assistant
  // Priority: URL draft param > draftId from auto-save > data.id > localStorage
  const effectiveClaimId = urlDraftId || draftId || data?.id || localStorage.getItem('current_draft_id') || '';

  // Reset wizard state
  const reset = async () => {
    await deleteDraft();
    setStep(1);
    setData({
      client_name: '',
      claimant_name: '',
      business_address: '',
      zone: '',
      business_type: '',
      financial_data: {}
    });
  };

  // Compute outstanding items from financial data
  const computeOutstandingItems = () => {
    let reviewCount = 0;
    let anomalyCount = 0;
    
    // Count transactions needing review from financial_data
    const financialData = data.financial_data || {};
    Object.values(financialData).forEach((period: any) => {
      if (period?.transactions) {
        period.transactions.forEach((txn: any) => {
          if (txn.needs_review) reviewCount++;
        });
      }
    });
    
    // Get anomaly count from cached analysis or data
    const cachedAnomalies = data.cached_anomaly_analysis?.anomalies || [];
    const resolvedAnomalies = data.resolved_anomalies || [];
    const resolvedIds = new Set(resolvedAnomalies.map((a: any) => a.id));
    anomalyCount = cachedAnomalies.filter((a: any) => !resolvedIds.has(a.id)).length;
    
    // Also check direct anomaly_count if set
    if (data.anomaly_count !== undefined) {
      anomalyCount = data.anomaly_count;
    }
    if (data.review_count !== undefined) {
      reviewCount = data.review_count;
    }
    
    return { reviewCount, anomalyCount };
  };
  
  const { reviewCount, anomalyCount } = computeOutstandingItems();

  // Common step props
  const commonProps = {
    data,
    isSaving,
    lastSaved,
    onSave: saveNow,
    goToStep,
    highestCompletedStep,
    claimId: effectiveClaimId,
  };
  
  // Props for review/final steps that need outstanding item counts
  const reviewProps = {
    ...commonProps,
    reviewCount,
    anomalyCount,
  };

  // Render the appropriate step based on current step and calculation_mode
  const renderStep = () => {
    const isFutureClosure = data.calculation_mode === 'future_closure';
    console.log(`🎯 renderStep called: step=${step}, isFutureClosure=${isFutureClosure}`);
    
    switch (step) {
      case 1:
        return (
          <SidebarStep1 
            onNext={next} 
            {...commonProps}
          />
        );
      
      case 2:
        return (
          <SidebarStep2 
            onNext={next} 
            onBack={back}
            onDataUpdate={(updates) => setData(prev => ({ ...prev, ...updates }))}
            {...commonProps}
          />
        );
      
      case 3:
        return (
          <SidebarStep3
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            initialSection={urlSection}
            {...commonProps}
          />
        );
      
      case 4:
        if (isFutureClosure) {
          return (
            <SidebarStep4_FutureClosure
              onNext={next}
              onBack={back}
              onDataUpdate={updateData}
              {...commonProps}
            />
          );
        }
        return (
          <SidebarStep4
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            {...commonProps}
          />
        );
      
      case 5:
        if (isFutureClosure) {
          return (
            <SidebarStep5_FutureClosure
              onNext={next}
              onBack={back}
              onDataUpdate={updateData}
              {...commonProps}
            />
          );
        }
        return (
          <SidebarStep5
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            {...commonProps}
          />
        );
      
      case 6:
        if (isFutureClosure) {
          return (
            <SidebarStep6_FutureClosure
              onBack={back}
              onComplete={reset}
              {...reviewProps}
            />
          );
        }
        return (
          <SidebarStep6
            onBack={back}
            onComplete={reset}
            {...reviewProps}
          />
        );
      
      case 7:
        if (isFutureClosure) {
          return (
            <SidebarStep7_FutureClosure
              onBack={back}
              onComplete={reset}
              {...reviewProps}
            />
          );
        }
        return (
          <SidebarStep7
            onBack={back}
            onComplete={reset}
            {...reviewProps}
          />
        );
      
      default:
        return null;
    }
  };

  // Loading state for URL draft
  if (loadingUrlDraft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading claim data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Restored Data Banner */}
      {showRestoredBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 p-4 bg-blue-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span>📋</span>
            <div>
              <p className="font-medium">Previous session restored</p>
              <p className="text-blue-100 text-sm">Continue where you left off or start fresh.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowRestoredBanner(false)}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-400 rounded-lg text-sm font-medium"
              data-testid="continue-session-btn"
            >
              Continue
            </button>
            <button
              onClick={clearAndStartFresh}
              className="px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg text-sm font-medium"
              data-testid="start-fresh-btn"
            >
              Start Fresh
            </button>
          </div>
        </div>
      )}
      
      {/* Main wizard content */}
      <div className={showRestoredBanner ? 'pt-20' : ''}>
        {renderStep()}
      </div>
    </>
  );
}
